import React, { useEffect } from 'react'
import { Outlet, useLocation } from 'react-router'
import styled from 'styled-components'

import type { FilterState } from 'features/filters/store'
import { useFilterStore } from 'features/filters/store'
import { useFeatureFlags } from 'features/project'
import { useChameleonProject } from 'features/project/hooks/use-chameleon-project'
import { useMixpanelProject } from 'features/project/hooks/use-mixpanel-project'
import { useProjectRedirects } from 'features/project/hooks/use-project-redirects'
import { useCurrentRoute } from 'hooks'
import { getScreenIdFromPath, isScreenStatePage } from 'routes/builders'

export const ProjectContainer = React.memo(() => {
  const location = useLocation()

  // --- Hooks: global lifecycle ---
  useMixpanelProject()
  useChameleonProject()
  useProjectRedirects()

  // hasLoadedFilters fallback: set true when there is no hash to hydrate
  // (non-filter pages, or filter pages with no hash in the URL).
  // Feature-specific hooks handle the case where a hash IS present.
  const route = useCurrentRoute()
  const setHasLoadedFilters = useFilterStore(
    (state: FilterState) => state.setHasLoadedFilters,
  )
  const isNonFilterPage = route.isFocus || route.isPDP || route.isAskYogi
  const currentFilterHash = getScreenIdFromPath(location.pathname)
  const isOnFilterPageWithoutHash =
    isScreenStatePage(location.pathname) && !currentFilterHash
  const isOnNonFilterPage =
    !isScreenStatePage(location.pathname) || isNonFilterPage

  useEffect(() => {
    if (isOnFilterPageWithoutHash || isOnNonFilterPage) {
      setHasLoadedFilters(true)
    }
  }, [isOnFilterPageWithoutHash, isOnNonFilterPage, setHasLoadedFilters])

  // --- Hooks: data prefetch ---
  useFeatureFlags()

  return (
    <Wrapper>
      <Outlet />
    </Wrapper>
  )
})

ProjectContainer.displayName = 'ProjectContainer'

const Wrapper = styled.div`
  height: 100%;
`
